import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { elements } from './sdb-elements.js';
/**
 * Progressbar element, used to display the progress of a user, skill or activity
 *
 * @prop {Boolean} secondary - optional, used to display the bar in the secondary theme color
 * @prop {Boolean} tertiary - optional, used to dislpay the bar in the tertiary theme color
 * @prop {Number} progress - used to define the progress percentage
 * @prop {Boolean} showPercentage - optional, used to show the percentage in the progressbar
 * @prop {Boolean} showLabel - optional, used to show the percentage right next to the progressbar
 * @prop {Boolean} lightBar - optional, used for a light progressbar on darker backgrounds
 * @prop {Boolean} smallBar - optional, used to display the entire progressbar with a smaller height (listview)
 * @prop {Boolean} noBar - optional, hides the background bar (only to be used on white backgrounds, e.g. cards)
 * @prop {Number} value - optional, used to define the actual progress value above the progressbar
 * @prop {Number} total - optional, used to define the word total (so it can be translated in frontend)
 * @prop {String} icon - optional, used to define an icon to display in front of the progressbar details
 * @prop {Number} totalValue - optional, used to define the total value (e.g. 25 out of 100)
 * @prop {String} mobileLabel - optional, used to define a label (title) only shown on mobile devices (directly above progressbar)
 * @prop {Boolean} smallText - optional, used to display the value and totalvalue in a smaller font-size
 * @prop {Boolean} darkText - optional, used to display the value and totalvalue in a dark color
 * @prop {Boolean} showEmptyBar - optional, used to display the bar even when there is no progress
 */
let SDBContentProgressBar = class SDBContentProgressBar extends LitElement {
    constructor() {
        super(...arguments);
        this.secondary = false;
        this.tertiary = false;
        this.progress = 0;
        this.showpercentage = false;
        this.percentageLabel = false;
        this.lightbar = false;
        this.nobar = false;
        this.icon = '';
        this.totalvalue = 0;
        this.total = '';
        this.smalltext = false;
        this.darktext = false;
        this.mobileLabel = '';
        this.showEmptyBar = false;
    }
    render() {
        return html `
            ${this.progress > 0 || this.totalvalue > 0 || this.showEmptyBar
            ? html `
                      <!-- DEFAULT COLOR FOR 100% -->
                      ${this.progress == 100
                ? html `<style>
                                .progress {
                                    background-color: var(
                                        --progress-100
                                    ) !important;
                                }
                            </style>`
                : html ``}

                      <!-- PROGRESSBAR DETAILS (OPTIONAL) -->

                      <div id="progressDetails">
                          ${this.icon
                ? html `<sdb-content-icon
                                    part="icon"
                                    size="20"
                                    .icon=${this.icon}
                                ></sdb-content-icon>`
                : html ``}
                          ${this.value !== null && this.value !== undefined
                ? html `<span part="value" class="value show"
                                    >${this.value}</span
                                >`
                : html ``}
                          ${this.totalvalue
                ? html `<span
                                    part="total-value"
                                    class="totalvalue"
                                >
                                    / ${this.totalvalue}
                                    <span class="totalValue"
                                        >${this.total}</span
                                    ></span
                                >`
                : html ``}
                      </div>

                      <!-- LABEL ONLY SHOWN ON MOBILE -->

                      ${this.mobileLabel
                ? html `<div id="mobileLabel"><span>${this.mobileLabel}</span></div></div>`
                : html ``}

                      <!-- PROGRESSBAR -->

                      <section id="progressbar" part="progressbar">
                          ${this.progress == 0 && this.showpercentage
                ? html `<div
                                    part="percentage"
                                    class="percentageIndicator"
                                    style="width:30px;left:calc(${Math.round(this.progress)}%);"
                                >
                                    ${Math.round(this.progress)}%
                                </div>`
                : html ``}
                          ${this.progress > 0 && this.progress <= 14
                ? html `
                                    ${this.showpercentage
                    ? html `<div
                                              part="percentage"
                                              class="percentageIndicator"
                                              style="width:30px;left:calc(${Math.round(this.progress)}% + 5px);"
                                          >
                                              ${Math.round(this.progress)}%
                                          </div>`
                    : html ``}
                                `
                : html ``}
                          ${this.progress > 14
                ? html `
                                    ${this.showpercentage
                    ? html `<div
                                              part="percentage"
                                              class="percentageIndicator"
                                              style="width:calc(${Math.round(this.progress)}% - 5px);left:0;"
                                          >
                                              ${Math.round(this.progress)}%
                                          </div>`
                    : html ``}
                                `
                : html ``}
                          ${this.progress == 0
                ? html ``
                : html `<div
                                    part="progress"
                                    class="progress"
                                    style="width:${this.progress}%;"
                                ></div>`}
                      </section>

                      ${this.percentageLabel
                ? html `<div class="percentageLabel">
                                ${Math.round(this.progress)}%
                            </div>`
                : html ``}
                  `
            : html ``}
        `;
    }
};
SDBContentProgressBar.styles = [
    elements,
    css `
            :host {
                margin: 10px 0;
                height: auto;
                min-width: 100%;
                display: grid;
                grid-auto-rows: fit-content;
                grid-template-columns: 1fr;
                grid-auto-flow: row;
                grid-template-areas:
                    'details details'
                    'label label'
                    'progress percentage';
            }

            :host([lightbar]) #progressbar {
                background: var(
                    --sdb-content-progressbar-lightbar-background,
                    rgba(255, 255, 255, 0.4)
                );
            }

            :host([nobar]) #progressbar {
                background: transparent;
            }

            :host #progressbar div.percentageIndicator {
                color: var(--text-light);
            }

            :host([smallbar]) #progressbar div.percentageIndicator {
                font-size: 0.5em;
            }

            :host([darktext]) #progressbar div.percentageIndicator {
                color: var(--text-dark);
            }

            :host([lightbar]) #progressbar div.percentageIndicator {
                color: var(--text-light);
            }

            :host([secondary]) #progressbar div.progress {
                background-color: var(--progress-secondary);
            }

            :host([tertiary]) #progressbar div.progress {
                background-color: var(--progress-tertiary);
            }

            #progressbar {
                grid-area: progress;
                display: block;
                position: relative;
                width: 100%;
                height: 20px;
                line-height: 18px;
                border-radius: 25px;
                background: var(
                    --sdb-content-progressbar-bar-background,
                    rgba(0, 0, 0, 0.2)
                );
                margin: 5px 0;
            }

            section#progressbar > div.progress {
                display: inline-block;
                height: 100%;
                min-width: 15px;
                border-radius: 20px;
                text-align: right;
                text-indent: 10px;
                line-height: 20px;
                color: white;
                direction: rtl;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: clip;
                background-color: var(--progress-primary);
            }

            .percentageLabel {
                grid-area: percentage;
                margin-left: 10px;
                font-weight: 300;
                font-size: 1em;
                color: var(
                    --sdb-content-progressbar-percentagelabel-color,
                    --text-primary
                );
                line-height: 28px;
            }

            :host([smallbar]) {
                margin: 0;
            }

            :host([smallbar]) .percentageLabel {
                height: 10px;
                line-height: 9px;
                border-radius: 10px;
            }

            :host([smallbar]) #progressbar {
                height: 10px;
                line-height: 9px;
                border-radius: 10px;
                margin: 0;
            }

            :host([smallbar]) #progressbar > div.progress {
                line-height: 10px;
            }

            :host([smallBar]) #progressDetails > span {
                font-size: 1em;
                font-weight: 300;
                vertical-align: text-top;
                line-height: 1;
                margin-bottom: 4px;
            }

            #progressDetails {
                grid-area: details;
                display: inline-flex;
                align-items: baseline;
                width: 100%;
                height: auto;
                padding-left: 3px;
            }

            #mobileLabel {
                display: none;
            }

            @media only screen and (max-width: 767px) {
                /* For mobile phones: */
                #mobileLabel {
                    grid-area: label;
                    display: block;
                    width: 100%;
                    height: 25px;
                }

                #mobileLabel span {
                    width: 100%;
                    display: block;
                    margin-top: 5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    font-size: 0.9em;
                    font-weight: 300;
                }

                .totalValue {
                    display: none;
                }
            }

            .percentageIndicator {
                display: block;
                position: absolute;
                overflow: hidden;
                text-align: right;
            }

            #progressDetails sdb-content-icon {
                margin-right: 5px;
            }

            #progressDetails span {
                line-height: 1;
                vertical-align: text-bottom;
            }

            #progressDetails span.value {
                font-size: 1.8em;
                font-weight: 500;
                margin-right: 3px;
                display: none;
            }

            #progressDetails span.value.show {
                display: inline-flex;
            }

            :host([smalltext]) #progressDetails span {
                display: inline-flex;
                font-weight: 300;
                font-size: 1em;
                line-height: 1;
                vertical-align: text-bottom;
            }

            #progressDetails span.totalvalue {
                font-size: 1.2em;
                font-weight: 300;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                width: fit-content;
                max-width: 210px;
                display: inline-flex;
            }
        `,
];
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "secondary", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "tertiary", void 0);
__decorate([
    property({ type: Number })
], SDBContentProgressBar.prototype, "progress", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "showpercentage", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "percentageLabel", void 0);
__decorate([
    property({ type: Boolean }) //should be used on colored backgrounds (summaryitems)
], SDBContentProgressBar.prototype, "lightbar", void 0);
__decorate([
    property({ type: Boolean }) //should be used on white backgrounds (cards, lists and accordions)
], SDBContentProgressBar.prototype, "nobar", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressBar.prototype, "icon", void 0);
__decorate([
    property({ type: Number })
], SDBContentProgressBar.prototype, "value", void 0);
__decorate([
    property({ type: Number })
], SDBContentProgressBar.prototype, "totalvalue", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressBar.prototype, "total", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "smalltext", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "darktext", void 0);
__decorate([
    property({ type: String })
], SDBContentProgressBar.prototype, "mobileLabel", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentProgressBar.prototype, "showEmptyBar", void 0);
SDBContentProgressBar = __decorate([
    customElement('sdb-content-progressbar')
], SDBContentProgressBar);
export { SDBContentProgressBar };
//# sourceMappingURL=sdb-content-progressbar.js.map